import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { History, Loader2 } from "lucide-react";

const PHASE_LABEL = {
  received:         "Received",
  fcp_work:         "False Ceiling / Plumbing Work",
  carcass_waiting:  "Carcass Delivery Waiting",
  carcass_assembly: "Carcass Assembly",
  manual_work:      "Manual Work",
  handed_over:      "Handed Over",
};

const PHASE_TONE = {
  received:         "border-gray-500/40 text-gray-400 bg-gray-500/5",
  fcp_work:         "border-sky-500/40 text-sky-300 bg-sky-500/5",
  carcass_waiting:  "border-amber-500/40 text-amber-300 bg-amber-500/5",
  carcass_assembly: "border-[#d4af37]/40 text-[#d4af37] bg-[#d4af37]/5",
  manual_work:      "border-orange-500/40 text-orange-300 bg-orange-500/5",
  handed_over:      "border-emerald-500/40 text-emerald-300 bg-emerald-500/5",
};

/** Vertical timeline of every automatic phase transition on an execution
 *  project. Each entry is appended by the backend after a stage-status
 *  update — this component is purely read-only. */
export default function PhaseHistoryPanel({ projectId, currentPhase, currentPhaseLabel }) {
  const [rows, setRows] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/execution/projects/${projectId}/phase-history`);
      setRows(data.phase_history || []);
    } catch {
      setRows([]);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="iz-card p-5" data-testid="phase-history-panel">
      <div className="flex items-center justify-between mb-4">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] flex items-center gap-1">
          <History className="w-3.5 h-3.5"/> Phase Timeline
        </p>
        {currentPhase && (
          <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${PHASE_TONE[currentPhase] || ""}`}
                data-testid="phase-history-current">
            Now · {currentPhaseLabel || PHASE_LABEL[currentPhase] || currentPhase}
          </span>
        )}
      </div>

      {rows === null ? (
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Loader2 className="w-3.5 h-3.5 animate-spin"/> Loading…
        </div>
      ) : rows.length === 0 ? (
        <p className="text-xs text-gray-500 italic">
          No phase transitions yet — the project is still in{" "}
          <span className="text-white">
            {currentPhaseLabel || PHASE_LABEL[currentPhase] || "Received"}
          </span>.
        </p>
      ) : (
        <ol className="relative border-l border-[#262626] pl-4 space-y-3"
            data-testid="phase-history-list">
          {[...rows].reverse().map((e) => (
            <li key={e.id} className="relative"
                data-testid={`phase-history-entry-${e.id}`}>
              <span className="absolute -left-[22px] top-1 w-3 h-3 rounded-full bg-[#d4af37] border-2 border-[#050505]"/>
              <p className="text-sm text-white">
                Entered{" "}
                <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ml-1 ${PHASE_TONE[e.to] || ""}`}>
                  {PHASE_LABEL[e.to] || e.to}
                </span>
              </p>
              <p className="text-[11px] text-gray-500 mt-0.5">
                {new Date(e.at).toLocaleString("en-IN",
                  { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                {" · "}
                by {e.by_user_name || "—"}
                {e.trigger_stage_key && (
                  <span className="text-gray-600">
                    {" · "}triggered by <span className="text-gray-400">
                      {e.trigger_stage_key.replace(/_/g, " ")} → {e.trigger_stage_status}
                    </span>
                  </span>
                )}
              </p>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
